import * as THREE from "three";
import debug from "debug";

const log = debug("app:ur");

/**
 * Class responsible for managing the rotation of objects in a 3D scene.
 * @category Experience
 * @subcategory Manipulation
 */
class RotateManager {
  /**
   * Constructs a RotateManager instance.
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.scene;
    this.camera = this.experience.camera.instance;
    this.raycaster = this.experience.raycaster;
    this.mouse = new THREE.Vector2();
    this.selectedObjects = [];
    this.rotationStateRadians = 0;
    this.eventHub = this.experience.eventHub;
    this.lastTap = 0;
    this.selectedGroup = null;

    // Bind handlers
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleRotateObjects = this.handleRotateObjects.bind(this);
    this.rotateObjects = this.rotateObjects.bind(this);

    this.init();
  }

  /**
   * Initializes the RotateManager by setting up event listeners.
   */
  init() {
    this.eventHub.on("lockEvents", (lock) =>
      lock ? this.disableListeners() : this.enableListeners()
    );
    this.enableListeners();
  }

  cleanup() {
    log("Cleaning up RotateManager...");

    // Remove all event listeners
    this.disableListeners();
    this.eventHub.off("globalMouseUp", this.handleMouseUp);
    this.eventHub.off("lockEvents");

    // Clear object references
    this.selectedObjects = [];
    this.selectedGroup = null;
    this.rotationStateRadians = 0;
    this.lastTap = 0;

    log("RotateManager cleanup complete.");
}

  /**
   * Enables event listeners for rotation-related events.
   */
  enableListeners() {
    log("listeners enabled");
    this.eventHub.on("globalKeyDown", this.handleKeyDown);
    this.eventHub.on("globalTouchStart", this.handleTouchStart);
    this.eventHub.on("globalMouseUp", this.handleMouseUp);
    this.eventHub.on("rotateObjects", this.handleRotateObjects);
  }

  /**
   * Disables event listeners for rotation-related events.
   */
  disableListeners() {
    log("listeners disabled");
    this.eventHub.off("globalKeyDown", this.handleKeyDown);
    this.eventHub.off("globalTouchStart", this.handleTouchStart);
    this.eventHub.off("globalMouseUp", this.handleMouseUp);
    this.eventHub.off("rotateObjects", this.handleRotateObjects);
  }

  /**
   * Handles object rotation based on an event payload.
   * @param {Object} payload - The rotation payload.
   * @param {number} payload.angle - The rotation angle in degrees.
   * @param {Array<THREE.Object3D>} payload.objects - The objects to rotate.
   */
  async handleRotateObjects({ angle, objects }) {
    if (typeof angle !== "number" || !Array.isArray(objects)) {
      log("Invalid payload format for rotateObjects:", { angle, objects });
      return;
    }
    if (!objects.length) {
      log("No valid objects found to rotate.");
      return;
    }
    await this.rotateObjects(angle, objects, false);
  }

  /**
   * Rotates the currently selected objects by the given angle.
   * @param {number} angleDegrees - The rotation angle in degrees.
   * @param {boolean} shouldLog - Whether to log the rotation event.
   */
  async rotateSelectedObjects(angleDegrees, shouldLog) {
    const selectedObjects = Array.from(this.experience.selectedObjects); // Convert Set to Array
    log("selectedObjects", selectedObjects);
    if (!selectedObjects || !selectedObjects.length) {
      log("No selected objects available for rotation.");
      return;
    }

    await this.rotateObjects(angleDegrees, selectedObjects, shouldLog);
  }

  /**
   * Rotates a group of objects around their calculated center by the given angle.
   * @param {number} angleDegrees - The rotation angle in degrees.
   * @param {Array<THREE.Object3D>} objects - The objects to rotate.
   * @param {boolean} shouldLog - Whether to log the rotation event.
   */
  async rotateObjects(angleDegrees, objects, shouldLog = false) {
    log("objects", objects, angleDegrees);
    const angleRadians = THREE.MathUtils.degToRad(angleDegrees);

    // Calculate the center of the group
    const center = this.calculateCenter(objects);
    this.applyRotation(angleRadians, objects, center);

    if (shouldLog) {
      const logData = this.prepareEventLog(objects, angleDegrees);
      this.eventHub.emit("logEvent", {
        eventType: "rotate",
        data: logData,
      });
    }
    this.eventHub.emit("xmd");
  }

  /**
   * Calculates the center of a group of objects.
   * @param {Array<THREE.Object3D>} objects - The objects to calculate the center for.
   * @returns {THREE.Vector3} - The calculated center position.
   */
  calculateCenter(objects) {
    const centroid = new THREE.Vector3();
    objects.forEach((object) => centroid.add(object.position));
    centroid.divideScalar(objects.length);

    // Round to the nearest whole value
    centroid.x = Math.round(centroid.x);
    centroid.y = Math.round(centroid.y);
    centroid.z = Math.round(centroid.z);

    return centroid;
  }

  /**
   * Applies a rotation to a group of objects around a given center.
   * @param {number} angleRadians - The rotation angle in radians.
   * @param {Array<THREE.Object3D>} objects - The objects to rotate.
   * @param {THREE.Vector3} center - The center of rotation.
   */
  applyRotation(angleRadians, objects, center) {
    objects.forEach((object) => {
      const offset = new THREE.Vector3().subVectors(object.position, center);

      // Apply rotation around the center
      const rotatedOffset = offset.applyAxisAngle(
        new THREE.Vector3(0, 1, 0),
        angleRadians
      );
      object.position.copy(center.clone().add(rotatedOffset));
      object.rotation.y += angleRadians;
      object.updateMatrixWorld(true);

      // Adjust text when part is upside down (180 degrees)
      this.adjustTextForUpsideDown(object);
    });
  }

  /**
   * Adjusts text meshes to remain readable when part is upside down.
   * If part is at 180 degrees, flips the text both vertically and horizontally.
   * @param {THREE.Object3D} group - The group containing text meshes.
   */
  adjustTextForUpsideDown(group) {
    if (!group || !group.children) return;

    // Get the normalized angle of the part (0, 90, 180, or 270 degrees)
    const normalizedAngle = this.getNormalizedAngle(group);

    // Check if part is upside down (180 degrees)
    const isUpsideDown = normalizedAngle === 180;

    group.traverse((child) => {
      // Find text meshes by name or userData type
      if (
        (child.name === "partNumber" || child.name === "MfgPart") &&
        child.userData?.type === "textGeometry"
      ) {
        if (isUpsideDown) {
          // Store original position and scale if not already stored
          if (child.userData.originalPosition === undefined) {
            child.userData.originalPosition = child.position.clone();
            child.userData.originalScale = child.scale.clone();
          }
          
          // Temporarily restore original scale to get accurate bounding box
          child.scale.copy(child.userData.originalScale);
          child.updateMatrixWorld(true);
          
          // Get the text's bounding box to calculate its width
          // Compute bounding box from the original (unflipped) state
          const bbox = new THREE.Box3().setFromObject(child);
          const textWidth = bbox.max.x - bbox.min.x;
          
          // When part is upside down, flip text both vertically and horizontally
          child.scale.y = -1; // Vertical flip
          child.scale.x = -1; // Horizontal flip
          
          // Shift the text right by its width to compensate for the mirroring
          // When scale.x = -1, the text is mirrored around its origin,
          // so we need to shift it by the width to keep it inside the part
          const originalPos = child.userData.originalPosition;
          child.position.x = originalPos.x + textWidth;
          child.position.z = originalPos.z;
          child.position.y = originalPos.y;
        } else {
          // When part is not upside down, restore original scale and position
          if (child.userData.originalScale) {
            child.scale.copy(child.userData.originalScale);
          } else {
            child.scale.x = 1;
            child.scale.y = 1;
            child.scale.z = 1;
          }
          
          // Restore original position if it was stored
          if (child.userData.originalPosition) {
            child.position.copy(child.userData.originalPosition);
          }
        }
        
        child.updateMatrixWorld(true);
      }
    });
  }

  /**
   * Gets the normalized angle of an object (0, 90, 180, or 270 degrees).
   * @param {THREE.Object3D} object - The object to get the angle from.
   * @returns {number} - The normalized angle in degrees.
   */
  getNormalizedAngle(object) {
    const worldQuaternion = new THREE.Quaternion();
    object.getWorldQuaternion(worldQuaternion);
    const worldRotation = new THREE.Euler().setFromQuaternion(
      worldQuaternion,
      "YZX"
    );
    const worldRotationDegrees =
      Math.round(THREE.MathUtils.radToDeg(worldRotation.y) / 90) * 90;
    return (worldRotationDegrees + 360) % 360;
  }

  /**
   * Prepares data for logging a rotation event.
   * @param {Array<THREE.Object3D>} objects - The rotated objects.
   * @param {number} angle - The rotation angle in degrees.
   * @returns {Object} - The log data.
   */
  prepareEventLog(objects, angle) {
    const objectIds = objects
      .map((object) => object.userData?.objectId)
      .filter(Boolean);
    return { angle, objects: objectIds };
  }

  /**
   * Handles key press events for rotating selected objects.
   * @param {KeyboardEvent} event - The key press event.
   */
  handleKeyDown(event) {
    const key = event.key.toLowerCase();
    if ((event.ctrlKey || event.metaKey) && (key === "l" || key === "r")) {
      event.preventDefault();
      const angle = key === "l" ? 90 : -90;
      this.rotateSelectedObjects(angle, true);
    } else if (key === "l" || key === "r") {
      const angle = key === "l" ? 90 : -90;
      this.rotateSelectedObjects(angle, true);
    }
  }

  /**
   * Handles double-click events to rotate objects.
   * @param {MouseEvent} event - The double-click event.
   */
  handleDoubleClick(event) {
    event.preventDefault();
    this.updateMousePosition(event.clientX, event.clientY);
    this.raycaster.setFromCamera(this.mouse, this.camera);
    this.checkIntersectionsAndRotate(-90, true);
  }

  /**
   * Handles touch start events for rotating objects.
   * @param {TouchEvent} event - The touch start event.
   */
  handleTouchStart(event) {
    event.preventDefault();
    const currentTime = new Date().getTime();
    const tapLength = currentTime - this.lastTap;
    if (tapLength < 500 && tapLength > 0) {
      this.updateMousePosition(
        event.touches[0].clientX,
        event.touches[0].clientY
      );
      this.raycaster.setFromCamera(this.mouse, this.camera);
      this.checkIntersectionsAndRotate(90, true);
    }
    this.lastTap = currentTime;
  }

  /**
   * Handles mouse up events to reset selected group.
   */
  handleMouseUp() {
    this.resetGroup();
  }

  /**
   * Updates the mouse position based on client coordinates.
   * @param {number} clientX - The X-coordinate of the mouse.
   * @param {number} clientY - The Y-coordinate of the mouse.
   */
  updateMousePosition(clientX, clientY) {
    this.mouse.x = (clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(clientY / window.innerHeight) * 2 + 1;
  }

  /**
   * Resets the selected objects and group.
   */
  resetGroup() {
    this.selectedObjects = [];
    this.selectedGroup = null;
  }
}

export default RotateManager;
